import { useEffect, useState, useMemo, forwardRef, useImperativeHandle } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import ProductCardModal from "./ProductCardModal";
import AddQrCodeModal from "./AddQrCodeModal";

const ProductListBC = forwardRef((props, ref) => {
  const [products, setProducts] = useState([]);
  const [options, setOptions] = useState({ retailers: [], sides: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [cardProduct, setCardProduct] = useState(null);
  const [addingQrTo, setAddingQrTo] = useState(null);
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "creator";

  // Sort state
  const [sortCol, setSortCol] = useState("productName");
  const [sortDir, setSortDir] = useState("asc");

  // Column filters — initialize from URL params if present
  const urlParams = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    productName: urlParams.get("productName") || "",
    upc: urlParams.get("upc") || "",
    itemId: urlParams.get("itemId") || "",
    retailer: urlParams.get("retailer") || "",
  });

  const fetchProducts = async () => {
    try {
      const [activeRes, deletedRes] = await Promise.all([
        api.get("/api/products"),
        api.get("/api/products/deleted"),
      ]);
      setProducts(activeRes.data);
      setDeletedProducts(deletedRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const restoreProduct = async (productId) => {
    try {
      await api.put(`/api/products/${productId}/restore`);
      fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: fetchProducts,
    openCard: (product) => setCardProduct(product),
  }));

  useEffect(() => {
    fetchProducts();
    api.get("/api/options").then((res) => setOptions(res.data)).catch(console.error);
  }, []);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const handleFilter = (col, value) => {
    setFilters((prev) => ({ ...prev, [col]: value }));
  };

  const filteredAndSorted = useMemo(() => {
    return products
      .filter((p) => {
        if (filters.productName && !(p.productName || "").toLowerCase().includes(filters.productName.toLowerCase())) return false;
        if (filters.upc) {
          const q = filters.upc.toLowerCase();
          const matchesUpc = (p.upc || "").toLowerCase().includes(q);
          const matchesShortCode = (p.urls || []).some((u) =>
            (u.urlId || "").toLowerCase().includes(q) ||
            (u.shortUrl || "").toLowerCase().includes(q)
          );
          if (!matchesUpc && !matchesShortCode) return false;
        }
        if (filters.itemId && !(p.itemId || "").toLowerCase().includes(filters.itemId.toLowerCase())) return false;
        if (filters.retailer && p.retailer !== filters.retailer) return false;
        return true;
      })
      .sort((a, b) => {
        let va, vb;
        switch (sortCol) {
          case "clicks":
            va = a.totalClicks; vb = b.totalClicks;
            return sortDir === "asc" ? va - vb : vb - va;
          case "codes":
            va = a.urls.length; vb = b.urls.length;
            return sortDir === "asc" ? va - vb : vb - va;
          default:
            va = (a[sortCol] || "").toLowerCase();
            vb = (b[sortCol] || "").toLowerCase();
            return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
        }
      });
  }, [products, filters, sortCol, sortDir]);

  const SortHeader = ({ col, label, width }) => (
    <th
      style={{ cursor: "pointer", userSelect: "none", width, whiteSpace: "nowrap" }}
      onClick={() => handleSort(col)}
     
    >
      {label}
      {sortCol === col && (
        <span className="ms-1" style={{ fontSize: "10px" }}>
          {sortDir === "asc" ? "\u25B2" : "\u25BC"}
        </span>
      )}
    </th>
  );

  const toggleProductSelection = (e, productId) => {
    e.stopPropagation();
    setSelectedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAllProducts = () => {
    if (selectedProducts.size === filteredAndSorted.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredAndSorted.map((p) => p._id)));
    }
  };

  const downloadSelectedProducts = async () => {
    if (selectedProducts.size === 0) return;
    setDownloading(true);

    try {
      const zip = new JSZip();
      const dateStr = new Date().toISOString().slice(0, 10);

      for (const product of filteredAndSorted) {
        if (!selectedProducts.has(product._id)) continue;
        if (product.urls.length === 0) continue;

        const prodName = (product.productName || "Product").replace(/\s+/g, "_");
        const itemId = product.itemId || "NOID";
        const upc = product.upc || "NOUPC";

        // Create a folder per product
        const folder = zip.folder(`${itemId}-${upc}-${prodName}`);

        for (const url of product.urls) {
          if (url.isDeprecated) continue;
          const response = await fetch(`/qr/${url.urlId}.svg`);
          if (response.ok) {
            const svgBlob = await response.blob();
            const side = (url.side || "other").charAt(0).toUpperCase() + (url.side || "other").slice(1);
            let fileName;
            if (url.qrFormat === "gs1" && url.gtin) {
              fileName = `${itemId}-GS1_${url.gtin}-${prodName}-${side}.svg`;
            } else {
              fileName = `${itemId}-${upc}-${prodName}-${url.urlId}-${side}.svg`;
            }
            folder.file(fileName, svgBlob);
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `QR_Codes-${dateStr}.zip`);
      setSelectedProducts(new Set());
    } catch (err) {
      console.error("ZIP download error:", err);
    } finally {
      setDownloading(false);
    }
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border spinner-border-sm" role="status" />
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="tl-card tl-card-compact mb-3" style={{ flexShrink: 0 }}>
        <div className="d-flex gap-2 align-items-end">
          <div style={{ flex: 3 }}>
            <label className="tl-form-label">Product Name</label>
            <input className="form-control form-control-sm tl-form-control" placeholder="Filter..." value={filters.productName} onChange={(e) => handleFilter("productName", e.target.value)} />
          </div>
          <div style={{ flex: 2 }}>
            <label className="tl-form-label">UPC / Short Code</label>
            <input className="form-control form-control-sm tl-form-control" placeholder="UPC or short code..." value={filters.upc} onChange={(e) => handleFilter("upc", e.target.value)} />
          </div>
          <div style={{ flex: 2 }}>
            <label className="tl-form-label">Item ID</label>
            <input className="form-control form-control-sm tl-form-control" placeholder="Filter..." value={filters.itemId} onChange={(e) => handleFilter("itemId", e.target.value)} />
          </div>
          <div style={{ flex: 2 }}>
            <label className="tl-form-label">Retailer</label>
            <select className="form-select form-select-sm tl-form-control" value={filters.retailer} onChange={(e) => handleFilter("retailer", e.target.value)}>
              <option value="">All</option>
              {options.retailers?.map((r) => (<option key={r} value={r}>{r}</option>))}
            </select>
          </div>
          {hasActiveFilters && (
            <div>
              <button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: "8px" }} onClick={() => setFilters({ productName: "", upc: "", itemId: "", retailer: "" })}>Clear</button>
            </div>
          )}
        </div>
      </div>

      {/* Grid card with summary and scrollable table */}
      <div className="tl-card tl-card-compact" style={{ display: "flex", flexDirection: "column", maxHeight: "60vh", minHeight: "200px", padding: 0 }}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--tl-border)", flexShrink: 0 }}>
          <span className="text-muted small">
            {filteredAndSorted.length} of {products.length} products &middot;{" "}
            {filteredAndSorted.reduce((s, p) => s + p.urls.length, 0)} QR codes
          </span>
        </div>
        <div style={{ overflow: "auto", flex: 1, fontSize: "13px" }}>
        <table className="tl-table mb-0">
          <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
            <tr>
              <th style={{ width: "30px" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={filteredAndSorted.length > 0 && selectedProducts.size === filteredAndSorted.length}
                  onChange={toggleSelectAllProducts}
                  style={{ cursor: "pointer" }}
                />
              </th>
              <SortHeader col="productName" label="Product Name" width="26%" />
              <SortHeader col="upc" label="UPC" width="14%" />
              <SortHeader col="itemId" label="Item ID" width="10%" />
              <SortHeader col="retailer" label="Retailer" width="10%" />
              <th style={{ width: "20%" }}>Notes</th>
              <SortHeader col="codes" label="QR Codes" width="8%" />
              <SortHeader col="clicks" label="Clicks" width="10%" />
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.map((product) => (
              <tr
                key={product._id}
                onClick={() => { setSelectedId(product._id); setCardProduct(product); }}
                className={selectedId === product._id ? "table-active" : ""}
                style={{ cursor: "pointer" }}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedProducts.has(product._id)}
                    onChange={(e) => toggleProductSelection(e, product._id)}
                    style={{ cursor: "pointer" }}
                  />
                </td>
                <td className=" text-truncate" style={{ color: "#0078d4" }}>
                  {product.productName}
                </td>
                <td className=" text-truncate">{product.upc || ""}</td>
                <td className=" text-truncate">{product.itemId || ""}</td>
                <td className=" text-truncate">{product.retailer || ""}</td>
                <td className=" text-truncate text-muted">
                  {product.notes ? product.notes.substring(0, 30) + (product.notes.length > 30 ? "..." : "") : ""}
                </td>
                <td className=" text-end">
                  {product.urls.length}
                  {product.urls.length > 0 && product.urls.every((u) => u.qrFormat === "legacy") && (
                    <span className="badge bg-light text-muted border ms-1" style={{ fontSize: "9px" }}>legacy</span>
                  )}
                </td>
                <td className=" text-end">{product.totalClicks.toLocaleString()}</td>
              </tr>
            ))}
            {filteredAndSorted.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-3">
                  No products match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Download bar */}
      {selectedProducts.size > 0 && (
        <div className="d-flex align-items-center justify-content-between p-2 mt-2 rounded" style={{ backgroundColor: "#e8f0fe" }}>
          <span style={{ fontSize: "13px" }}>
            {selectedProducts.size} product{selectedProducts.size !== 1 ? "s" : ""} selected
            ({filteredAndSorted.filter((p) => selectedProducts.has(p._id)).reduce((s, p) => s + p.urls.length, 0)} QR codes)
          </span>
          <button
            className="btn btn-sm btn-primary d-flex align-items-center gap-1"
            onClick={downloadSelectedProducts}
            disabled={downloading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {downloading ? "Zipping..." : "Download ZIP"}
          </button>
        </div>
      )}

      {/* Deleted products */}
      {canEdit && deletedProducts.length > 0 && (
        <div className="tl-card tl-card-compact mt-3">
          <button
            className="btn btn-sm btn-outline-secondary mb-2"
            onClick={() => setShowDeleted(!showDeleted)}
          >
            {showDeleted ? "Hide" : "Show"} Deleted Products ({deletedProducts.length})
          </button>
          {showDeleted && (
            <div className="border" style={{ fontSize: "13px", opacity: 0.7 }}>
              <table className="tl-table mb-0">
                <thead>
                  <tr style={{ backgroundColor: "#f8f9fa" }}>
                    <th style={{ width: "28%" }}>Product Name</th>
                    <th style={{ width: "14%" }}>UPC</th>
                    <th style={{ width: "12%" }}>Retailer</th>
                    <th style={{ width: "20%" }}>Deleted</th>
                    <th style={{ width: "16%" }}>Deleted By</th>
                    <th style={{ width: "10%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {deletedProducts.map((p) => (
                    <tr key={p._id}>
                      <td className=" text-truncate text-muted">{p.productName}</td>
                      <td className=" text-muted">{p.upc || ""}</td>
                      <td className=" text-muted">{p.retailer || ""}</td>
                      <td className=" text-muted small">
                        {p.deletedAt ? new Date(p.deletedAt).toLocaleString() : "—"}
                      </td>
                      <td className=" text-muted small">{p.deletedBy || "—"}</td>
                      <td className=" text-end">
                        <button
                          className="btn btn-link btn-sm p-0 text-decoration-none"
                          style={{ fontSize: "12px" }}
                          onClick={() => restoreProduct(p._id)}
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Product Card Modal (BC-style) */}
      {cardProduct && (
        <ProductCardModal
          product={cardProduct}
          options={options}
          canEdit={canEdit}
          onClose={() => setCardProduct(null)}
          onSaved={() => { setCardProduct(null); fetchProducts(); }}
          onAddQr={(product) => { setAddingQrTo(product); }}
          onRefresh={fetchProducts}
        />
      )}

      {addingQrTo && (
        <AddQrCodeModal
          productId={addingQrTo._id}
          productName={addingQrTo.productName}
          productUpc={addingQrTo.upc}
          productRetailer={addingQrTo.retailer}
          options={options}
          onClose={() => setAddingQrTo(null)}
          onSaved={async () => {
            const productId = addingQrTo._id;
            setAddingQrTo(null);
            await fetchProducts();
            // Re-open the card with fresh data
            try {
              const res = await api.get(`/api/products/${productId}`);
              setCardProduct(res.data);
            } catch (err) {
              console.error(err);
            }
          }}
        />
      )}
    </div>
  );
});

export default ProductListBC;
